import {Transition} from '@headlessui/react';
import {XIcon} from '@heroicons/react/solid';
import React, {Fragment, useEffect, useState} from 'react';
import {CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon, XCircleIcon} from "@heroicons/react/outline";

enum Type {INFO, SUCCESS, WARNING, ERROR}

type NotificationProps = {
    type: Type
    title: string
    duration?: number
    children: React.ReactNode
}

const icons: Record<Type, JSX.Element> = {
    [Type.INFO]: <InformationCircleIcon className="h-6 w-6 text-blue-400" aria-hidden="true"/>,
    [Type.SUCCESS]: <CheckCircleIcon className="h-6 w-6 text-green-400" aria-hidden="true"/>,
    [Type.WARNING]: <ExclamationCircleIcon className="h-6 w-6 text-yellow-400" aria-hidden="true"/>,
    [Type.ERROR]: <XCircleIcon className="h-6 w-6 text-red-400" aria-hidden="true"/>,
}

function Notification(props: NotificationProps) {
    const [show, setShow] = useState(true)

    const {type = Type.INFO, title, duration = 3000, children} = props

    useEffect(() => {
        if (show) {
            setTimeout(() => {
                setShow(false)
            }, duration)
        }
    }, [duration, show])

    return (
        <Transition
            appear={true}
            show={show}
            as={Fragment}
            unmount={true}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <div
                className="max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden">
                <div className="p-4">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            {icons[type]}
                        </div>
                        <div className="ml-3 w-0 flex-1 pt-0.5 break-words">
                            <p className="text-sm font-medium text-gray-900 capitalize">{title}</p>
                            <p className="mt-1 text-sm text-gray-500">{children}</p>
                        </div>
                        <div className="ml-4 flex-shrink-0 flex">
                            <button
                                className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand"
                                onClick={() => setShow(false)}
                            >
                                <span className="sr-only">Close</span>
                                <XIcon className="h-5 w-5" aria-hidden="true"/>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    );
}

Notification.types = Type

export default Notification;